/**
 * Bundle Section JavaScript
 * Handles bundle product selection with variant-based pricing
 */

// Global state
let PACK_SELECTION = {
  pack_items: [],
  sub_price: 0,
  price: 0,
  current_discount: 0,
  current_variant: null
};

/**
 * Find the bundle variant that matches the sachet count
 * @param {number} totalBags - Total number of sachets
 * @returns {object|null} - The matching variant or null
 */
function findVariantForSachetCount(totalBags) {
  if (!window.bundleData || !window.bundleData.reference_product || !window.bundleData.reference_product.variants) {
    console.error('Bundle Section - Bundle variants not available');
    return null;
  }

  const variants = window.bundleData.reference_product.variants;

  // Find variant where title contains the number matching totalBags
  // e.g., "8 sachets" for 8 bags, "12 sachets" for 12 bags
  const matchingVariant = variants.find(variant => {
    // Extract number from variant title
    const match = variant.title.match(/(\d+)/);
    if (match) {
      const variantCount = parseInt(match[1]);
      return variantCount === totalBags;
    }
    return false;
  });

  if (matchingVariant) {
    console.log(`Bundle Section - Found variant for ${totalBags} sachets:`, matchingVariant);
    return matchingVariant;
  }

  // If no exact match, find the closest variant below or equal to totalBags
  const sortedVariants = [...variants]
    .map(v => {
      const match = v.title.match(/(\d+)/);
      return {
        ...v,
        count: match ? parseInt(match[1]) : 0
      };
    })
    .filter(v => v.count > 0 && v.count <= totalBags)
    .sort((a, b) => b.count - a.count);

  if (sortedVariants.length > 0) {
    console.log(`Bundle Section - No exact match for ${totalBags} sachets, using closest:`, sortedVariants[0]);
    return sortedVariants[0];
  }

  console.warn(`Bundle Section - No suitable variant found for ${totalBags} sachets`);
  return null;
}

/**
 * Initialize pack items from bundle data
 */
function initPackItems() {
  const packItems = [];
  const passedIds = new Set();

  if (!window.bundleData || !window.bundleData.bundle_products) {
    console.error('Bundle Section - window.bundleData.bundle_products is not defined');
    return packItems;
  }

  for (let item of window.bundleData.bundle_products) {
    const variantId = item.variants[0].id;
    if (!passedIds.has(variantId)) {
      packItems.push({
        id: variantId,
        productId: item.id,
        handle: item.handle,
        title: item.title,
        quantity: 0,
        price: item.price,
        image: item.bundle_image || item.image,
      });
      passedIds.add(variantId);
    }
  }

  return packItems;
}

/**
 * Bundle Section Custom Element
 */
class BundleSection extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    console.log('Bundle Section - Initializing...');

    // Initialize state
    if (typeof window.bundleData === 'undefined') {
      console.error('Bundle Section - window.bundleData is not defined!');
      return;
    }

    console.log('Bundle Section - bundleData:', window.bundleData);

    PACK_SELECTION.pack_items = initPackItems();
    PACK_SELECTION.price = window.bundleData.reference_price || 0;
    PACK_SELECTION.sub_price = window.bundleData.reference_sub_price || 0;

    console.log('Bundle Section - PACK_SELECTION initialized:', PACK_SELECTION);

    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      this.initEventListeners();
      this.initShippingSwiper();
      this.initDeliveryDate();
      this.updateUI();
      console.log('Bundle Section - Ready!');
    }, 0);
  }

  initDeliveryDate() {
    const deliveryDateElement = this.querySelector('[data-delivery-date]');
    const deliveryDaysContainer = this.querySelector('[data-delivery-days]');

    if (deliveryDateElement && deliveryDaysContainer) {
      const businessDays = parseInt(deliveryDaysContainer.dataset.deliveryDays);
      const deliveryDate = this.calculateDeliveryDate(businessDays);
      deliveryDateElement.textContent = this.formatDeliveryDate(deliveryDate);
    }
  }

  calculateDeliveryDate(businessDays) {
    const today = new Date();
    let addedDays = 0;
    let currentDate = new Date(today);

    while (addedDays < businessDays) {
      currentDate.setDate(currentDate.getDate() + 1);

      // Check if it's a business day (Monday=1 to Friday=5)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        addedDays++;
      }
    }

    return currentDate;
  }

  formatDeliveryDate(date) {
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  }

  initShippingSwiper() {
    const shippingSwiper = this.querySelector('.bundle-checkout__shipping-swiper');
    if (shippingSwiper && typeof Swiper !== 'undefined') {
      new Swiper(shippingSwiper, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        pagination: {
          el: '.bundle-checkout__shipping-pagination',
          clickable: true,
        },
      });
    }
  }

  initEventListeners() {
    // Product CTA buttons
    this.querySelectorAll('.bundle-product-card__button').forEach(button => {
      button.addEventListener('click', this.handleAddProduct.bind(this));
    });

    // Quantity selectors
    this.querySelectorAll('.bundle-quantity-selector__button').forEach(button => {
      button.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    // Checkout button
    const checkoutBtn = this.querySelector('[data-checkout-button]');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', this.handleCheckout.bind(this));
    }

    // Subscription toggle
    const subOption = this.querySelector('[data-subscription-option]');
    if (subOption) {
      subOption.addEventListener('click', this.toggleSubscription.bind(this));
    }

    // Frequency selector
    const frequencySelector = this.querySelector('[data-frequency-selector]');
    if (frequencySelector) {
      const current = frequencySelector.querySelector('.bundle-subscription__frequency-current');
      current?.addEventListener('click', () => {
        frequencySelector.classList.toggle('is-open');
      });

      const options = frequencySelector.querySelectorAll('[data-frequency-option]');
      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleFrequencyChange(option);
          frequencySelector.classList.remove('is-open');
        });
      });
    }
  }

  handleAddProduct(e) {
    const button = e.currentTarget;
    const card = button.closest('.bundle-product-card');
    const handle = button.dataset.productHandle;

    // Find product in pack items
    const item = PACK_SELECTION.pack_items.find(p => p.handle === handle);
    if (item) {
      item.quantity = 1;

      // Update UI
      this.showQuantitySelector(card);
      this.updateUI();
    }
  }

  handleQuantityChange(e) {
    const button = e.currentTarget;
    const card = button.closest('.bundle-product-card');
    const quantityDisplay = card.querySelector('[data-quantity]');
    const action = button.dataset.action;

    // Get product handle from button
    const handle = button.dataset.productHandle;

    if (!handle) return;

    const item = PACK_SELECTION.pack_items.find(p => p.handle === handle);
    if (!item) return;

    if (action === 'increment') {
      item.quantity += 1;
    } else if (action === 'decrement' && item.quantity > 0) {
      item.quantity -= 1;

      if (item.quantity === 0) {
        this.showCTAButton(card);
      }
    }

    quantityDisplay.textContent = item.quantity;
    this.updateUI();
  }

  showQuantitySelector(card) {
    const ctaWrapper = card.querySelector('[data-cta-wrapper]');
    const quantityWrapper = card.querySelector('[data-quantity-wrapper]');
    const quantityDisplay = card.querySelector('[data-quantity]');
    const ctaButton = card.querySelector('.bundle-product-card__button');
    const handle = ctaButton?.dataset.productHandle;

    if (ctaWrapper) ctaWrapper.classList.add('hidden');
    if (quantityWrapper) {
      quantityWrapper.classList.remove('hidden');
      const item = PACK_SELECTION.pack_items.find(p => p.handle === handle);
      if (item && quantityDisplay) {
        quantityDisplay.textContent = item.quantity;
      }
    }
  }

  showCTAButton(card) {
    const ctaWrapper = card.querySelector('[data-cta-wrapper]');
    const quantityWrapper = card.querySelector('[data-quantity-wrapper]');

    if (ctaWrapper) ctaWrapper.classList.remove('hidden');
    if (quantityWrapper) quantityWrapper.classList.add('hidden');
  }

  toggleSubscription() {
    const subOption = this.querySelector('[data-subscription-option]');
    if (subOption) {
      subOption.classList.toggle('is-active');
      this.updatePricing();
    }
  }

  handleFrequencyChange(option) {
    const currentDisplay = this.querySelector('[data-current-frequency]');
    const sellingPlanId = option.dataset.sellingPlanId;
    const index = option.dataset.indexSellingPlan;

    if (currentDisplay) {
      currentDisplay.textContent = option.textContent.trim();
      currentDisplay.dataset.currentRecurring = index;
    }

    // Update active state
    this.querySelectorAll('[data-frequency-option]').forEach(opt => {
      opt.classList.remove('is-active');
    });
    option.classList.add('is-active');
  }

  calculateTotals() {
    let totalBags = 0;
    let totalPrice = 0;
    let compareAtPrice = null;
    let currentDiscount = 0;

    // Calculate total bags
    PACK_SELECTION.pack_items.forEach(item => {
      totalBags += item.quantity;
    });

    // Find the variant corresponding to the total bag count
    const matchingVariant = findVariantForSachetCount(totalBags);

    if (matchingVariant && totalBags > 0) {
      // Use the variant price
      const isSubscription = this.querySelector('[data-subscription-option]')?.classList.contains('is-active');

      if (isSubscription) {
        // Apply subscription discount to variant price
        const subscriptionDiscount = window.bundleData?.subscription_discount || 1;
        totalPrice = (matchingVariant.price / 100) * subscriptionDiscount;
      } else {
        totalPrice = matchingVariant.price / 100;
      }

      // Get compare_at_price if available
      if (matchingVariant.compare_at_price) {
        compareAtPrice = matchingVariant.compare_at_price / 100;
      }

      // Store current variant
      PACK_SELECTION.current_variant = matchingVariant;

      console.log(`Bundle Section - Total bags: ${totalBags}, Variant: ${matchingVariant.title}, Price: ${totalPrice}€, Compare at: ${compareAtPrice}€`);
    } else {
      totalPrice = 0;
      compareAtPrice = null;
      PACK_SELECTION.current_variant = null;
    }

    // Update progress items based on thresholds (for visual progress)
    const progressItems = this.querySelectorAll('[data-progress-item]');
    progressItems.forEach(item => {
      const threshold = parseInt(item.dataset.threshold);
      if (totalBags >= threshold) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });

    PACK_SELECTION.current_discount = currentDiscount;

    return {
      totalBags,
      totalPrice,
      compareAtPrice,
      currentDiscount
    };
  }

  updateProgressBars() {
    const { totalBags } = this.calculateTotals();
    const progressItems = this.querySelectorAll('[data-progress-item]');

    // Group items by threshold to get unique thresholds
    const thresholdGroups = new Map();
    progressItems.forEach(item => {
      const threshold = parseInt(item.dataset.threshold);
      if (!thresholdGroups.has(threshold)) {
        thresholdGroups.set(threshold, []);
      }
      thresholdGroups.get(threshold).push(item);
    });

    // Sort thresholds
    const sortedThresholds = Array.from(thresholdGroups.keys()).sort((a, b) => a - b);

    // Update each group
    sortedThresholds.forEach((threshold, thresholdIndex) => {
      const items = thresholdGroups.get(threshold);
      let percentage = 0;

      if (thresholdIndex === 0) {
        // First threshold: 0 to threshold
        percentage = Math.min((totalBags / threshold) * 100, 100);
      } else {
        // Subsequent thresholds: previous threshold to current threshold
        const prevThreshold = sortedThresholds[thresholdIndex - 1];
        const range = threshold - prevThreshold;

        if (totalBags <= prevThreshold) {
          percentage = 0;
        } else if (totalBags >= threshold) {
          percentage = 100;
        } else {
          percentage = ((totalBags - prevThreshold) / range) * 100;
        }
      }

      // Apply percentage to all items with this threshold
      items.forEach(item => {
        const fill = item.querySelector('[data-progress-fill]');
        if (fill) {
          fill.style.width = `${percentage}%`;
        }
      });
    });
  }

  updateSelectedItemsPreview() {
    const previewContainer = this.querySelector('[data-preview-images]');
    if (!previewContainer) return;

    // Remove active class from all items first
    const allItems = previewContainer.querySelectorAll('[data-image-index]');
    allItems.forEach(item => {
      item.classList.remove('active');
      // Clear src to avoid flash of previous image
      const img = item.querySelector('.bundle-selected-preview__image');
      if (img) img.src = '';
    });

    // Show items with quantity > 0
    let displayIndex = 0;
    PACK_SELECTION.pack_items.forEach(item => {
      if (item.quantity > 0) {
        const previewItem = previewContainer.querySelector(`[data-image-index="${displayIndex}"]`);
        if (previewItem) {
          const img = previewItem.querySelector('.bundle-selected-preview__image');
          const badge = previewItem.querySelector('.bundle-selected-preview__quantity-badge');

          if (img && item.image) {
            img.src = item.image;
          }
          if (badge) badge.textContent = `x${item.quantity}`;

          previewItem.classList.add('active');
          displayIndex++;
        }
      }
    });
  }

  updateSelectedItemsPills() {
    const pillsContainers = this.querySelectorAll('[data-selected-pills]');
    const listContainer = this.querySelector('[data-selected-list]');

    if (!pillsContainers || pillsContainers.length === 0) return;

    let hasItems = false;

    // Update ALL containers with data-selected-pills
    pillsContainers.forEach(pillsContainer => {
      // Clear existing pills
      pillsContainer.innerHTML = '';

      // Create pills for items with quantity > 0
      PACK_SELECTION.pack_items.forEach(item => {
        if (item.quantity > 0) {
          hasItems = true;

          const pill = document.createElement('div');
          pill.className = 'bundle-section__mobile-selected-pill';

          pill.innerHTML = `
            <div class="bundle-section__mobile-selected-pill-image">
              <img src="${item.image}" alt="${item.title}">
            </div>
            <span class="bundle-section__mobile-selected-pill-text">${item.title} (${item.quantity})</span>
          `;

          pillsContainer.appendChild(pill);
        }
      });
    });

    // Show/hide mobile selected section based on whether there are items
    const mobileSelected = this.querySelector('.bundle-section__mobile-selected');
    if (mobileSelected) {
      if (hasItems) {
        mobileSelected.style.display = 'flex';
      } else {
        mobileSelected.style.display = 'none';
      }
    }

    // Show/hide desktop list based on whether there are items
    if (listContainer) {
      if (hasItems) {
        listContainer.classList.remove('hidden');
      } else {
        listContainer.classList.add('hidden');
      }
    }
  }

  updatePricing() {
    const { totalPrice, compareAtPrice, totalBags } = this.calculateTotals();
    const priceDisplay = this.querySelector('[data-total-price]');
    const comparePriceDisplay = this.querySelector('[data-compare-price]');

    if (priceDisplay) {
      priceDisplay.textContent = `${totalPrice.toFixed(2)}€`;
    }

    // Update compare_at_price display
    if (comparePriceDisplay) {
      if (compareAtPrice && compareAtPrice > totalPrice) {
        comparePriceDisplay.textContent = `${compareAtPrice.toFixed(2)}€`;
      } else {
        comparePriceDisplay.textContent = '';
      }
    }

    // Update per-bag price in subscription
    const unitPriceDisplay = this.querySelector('[data-unit-price]');
    if (unitPriceDisplay) {
      if (totalBags > 0) {
        const unitPrice = totalPrice / totalBags;
        unitPriceDisplay.textContent = `${unitPrice.toFixed(2)}€/sachet`;
      }
    }
  }

  updateCheckoutButton() {
    const { totalBags } = this.calculateTotals();
    const checkoutBtn = this.querySelector('[data-checkout-button]');
    const countdownText = this.querySelector('[data-countdown-text]');

    if (!checkoutBtn) return;

    const progressItems = this.querySelectorAll('[data-progress-item]');
    const firstThreshold = progressItems[0] ? parseInt(progressItems[0].dataset.threshold) : 8;

    // Update countdown text
    if (totalBags < firstThreshold) {
      const remaining = firstThreshold - totalBags;
      if (countdownText) {
        countdownText.textContent = `Sélectionner encore ${remaining} sachet${remaining > 1 ? 's' : ''}`;
      }
      checkoutBtn.classList.add('show-countdown');
      checkoutBtn.classList.add('disabled');
      checkoutBtn.disabled = true;
    } else {
      checkoutBtn.classList.remove('show-countdown');
      checkoutBtn.classList.remove('disabled');
      checkoutBtn.disabled = false;
    }
  }

  updateShippingProgress() {
    const shippingFill = this.querySelector('[data-shipping-progress]');
    const freeShippingAmount = parseFloat(this.dataset.freeShippingAmmount);
    const currentAmount = parseFloat(this.dataset.cartAmmount) / 100;
    const { totalPrice } = this.calculateTotals();
    const totalAmount = currentAmount + totalPrice;

    if (shippingFill && freeShippingAmount) {
      const percentage = Math.min((totalAmount / freeShippingAmount) * 100, 100);
      shippingFill.setAttribute('width', `${(percentage / 100) * 36}`);
    }
  }

  updateUI() {
    this.updateProgressBars();
    this.updateSelectedItemsPreview();
    this.updateSelectedItemsPills();
    this.updatePricing();
    this.updateCheckoutButton();
    this.updateShippingProgress();
  }

  async handleCheckout() {
    const { totalBags } = this.calculateTotals();
    if (totalBags === 0) {
      console.log('Bundle Section - No items to add');
      return;
    }

    // Check if we have a valid variant for the current bag count
    if (!PACK_SELECTION.current_variant) {
      console.error('Bundle Section - No variant found for current bag count:', totalBags);
      return;
    }

    console.log('Bundle Section - Adding bundle to cart...', PACK_SELECTION.pack_items);
    console.log('Bundle Section - Current variant:', PACK_SELECTION.current_variant);
    console.log('Bundle Section - Available selling plans:', window.selling_plans);

    const isSubscription = this.querySelector('[data-subscription-option]')?.classList.contains('is-active');
    console.log('Bundle Section - Is subscription:', isSubscription);

    // Build the content property string and _ids property
    const contentParts = [];
    const idParts = [];

    for (const item of PACK_SELECTION.pack_items) {
      // Find the product data to get short_title
      const productData = window.bundleData.bundle_products.find(p => p.handle === item.handle);
      const title = productData?.short_title || item.title;

      // Add to content string with quantity (only if quantity > 0)
      if (item.quantity > 0) {
        contentParts.push(`${item.quantity} x ${title}`);
      }

      // Add to _ids string (always, even if quantity is 0)
      idParts.push(`${item.id}:${item.quantity}`);
    }

    const contentString = contentParts.join(', ');
    const idsString = idParts.join(', ');

    console.log('Bundle Section - Content property:', contentString);
    console.log('Bundle Section - IDs property:', idsString);

    // Prepare the bundle product (reference product) for cart
    const referenceProduct = window.bundleData.reference_product;
    if (!referenceProduct) {
      console.error('Bundle Section - Reference product not found');
      return;
    }

    // Use the current variant ID based on bag count
    const cartItem = {
      id: PACK_SELECTION.current_variant.id,
      quantity: 1,
      properties: {
        'Contenu': contentString,
        '_ids': idsString
      }
    };

    console.log('Bundle Section - Cart item with properties:', JSON.stringify(cartItem, null, 2));

    // Add selling plan if subscription is active
    if (isSubscription && window.selling_plans && window.selling_plans[referenceProduct.handle]) {
      const currentRecurring = parseInt(this.querySelector('[data-current-frequency]')?.dataset.currentRecurring || '1');
      const productPlans = window.selling_plans[referenceProduct.handle].plans;

      console.log('Bundle Section - Reference product handle:', referenceProduct.handle);
      console.log('Bundle Section - Current recurring index:', currentRecurring);
      console.log('Bundle Section - Available plans for product:', productPlans);

      const sellingPlanId = productPlans[currentRecurring];

      console.log('Bundle Section - Selected selling plan ID:', sellingPlanId);

      if (sellingPlanId) {
        cartItem.selling_plan = sellingPlanId;
      }
    }

    console.log('Bundle Section - Cart item to add:', cartItem);

    try {
      // Add bundle product to cart
      const requestBody = JSON.stringify(cartItem);
      console.log('Bundle Section - Request body being sent:', requestBody);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      console.log('Bundle Section - Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Bundle Section - Cart add failed. Status:', response.status);
        console.error('Bundle Section - Error response:', errorText);
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      const cartData = await response.json();
      console.log('Bundle Section - Cart data received:', cartData);
      console.log('Bundle Section - Item added to cart:', cartData.items ? cartData.items[cartData.items.length - 1] : cartData);

      // Trigger cart update event to refresh sidecart
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));

      // Open sidecart by finding and clicking a cart trigger
      const cartTrigger = document.querySelector('[data-cart-trigger]');
      if (cartTrigger) {
        console.log('Bundle Section - Opening sidecart via cart trigger');
        cartTrigger.click();
      } else {
        // Fallback: try to open overlay directly
        const overlay = document.querySelector('#overlay');
        if (overlay) {
          console.log('Bundle Section - Opening sidecart directly');
          overlay.classList.add('is-open');
          document.body.classList.add('cart-open');
        }
      }

      console.log('Bundle Section - Checkout complete!');
    } catch (error) {
      console.error('Bundle Section - Failed to add bundle to cart:', error);
      console.error('Bundle Section - Error stack:', error.stack);
    }
  }
}

// Register custom element
if (customElements.get('bundle-section') === undefined) {
  customElements.define('bundle-section', BundleSection);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Set CSS viewport height variable
  document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
});

// Update viewport height on resize
window.addEventListener('resize', () => {
  document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
});
